import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfQuarter, endOfQuarter, startOfYear, endOfYear } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sidebar } from '@/components/Sidebar';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Wallet, Receipt, AlertTriangle, Plus, Trash2, Copy, Check, Upload, FileCheck } from 'lucide-react';
import { currencySymbol, formatMoney } from '@/lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPENSE_CATEGORIES = [
  { id: 'supervision', label: 'Супервизия' },
  { id: 'therapy', label: 'Личная терапия' },
  { id: 'rent', label: 'Аренда кабинета' },
  { id: 'services', label: 'Сервисы и подписки' },
  { id: 'education', label: 'Обучение' },
  { id: 'advertising', label: 'Реклама' },
  { id: 'other', label: 'Прочее' },
];
const PERIODS = [
  { id: 'week', label: 'Неделя' },
  { id: 'month', label: 'Месяц' },
  { id: 'quarter', label: 'Квартал' },
  { id: 'year', label: 'Год' },
];
const PIE_COLORS = ['#d97706', '#059669', '#7c3aed', '#dc2626', '#2563eb', '#db2777'];

function getPeriodRange(period) {
  const now = new Date();
  switch (period) {
    case 'week': return [startOfWeek(now, { weekStartsOn: 1 }), endOfWeek(now, { weekStartsOn: 1 })];
    case 'quarter': return [startOfQuarter(now), endOfQuarter(now)];
    case 'year': return [startOfYear(now), endOfYear(now)];
    default: return [startOfMonth(now), endOfMonth(now)];
  }
}
const catLabel = (id) => EXPENSE_CATEGORIES.find(c => c.id === id)?.label || id;

export default function FinancePage() {
  const [period, setPeriod] = useState('month');
  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [expForm, setExpForm] = useState({ category: 'supervision', amount: '', description: '', date: format(new Date(), 'yyyy-MM-dd') });
  const [payingId, setPayingId] = useState(null);
  const [currency, setCurrency] = useState('RUB');
  const navigate = useNavigate();
  const fmtMoney = useCallback((value) => formatMoney(value, currency), [currency]);

  const fetchData = useCallback(async () => {
    try {
      const [from, to] = getPeriodRange(period);
      const dateFrom = format(from, 'yyyy-MM-dd');
      const dateTo = format(to, 'yyyy-MM-dd');
      const [sumRes, expRes, analyticsRes, settingsRes] = await Promise.all([
        api.get(`/finance/summary?date_from=${dateFrom}&date_to=${dateTo}`),
        api.get(`/expenses?date_from=${dateFrom}&date_to=${dateTo}`),
        api.get(`/finance/analytics`),
        api.get('/settings'),
      ]);
      setSummary(sumRes.data || {});
      setExpenses(Array.isArray(expRes.data) ? expRes.data : []);
      setAnalytics(analyticsRes.data || {});
      setCurrency(settingsRes.data?.currency || 'RUB');
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
      if (err.response?.status === 403) { navigate('/pricing'); return; }
    }
    setLoading(false);
  }, [period, navigate]);

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) { navigate('/login'); return; }
    fetchData();
  }, [navigate, fetchData]);

  const markPaid = async (id) => {
    setPayingId(id);
    try {
      const { data } = await api.put(`/appointments/${id}/mark-paid`, {});
      toast.success(data.deducted_from_balance ? `Оплачено (списано с депозита: ${formatMoney(data.amount, data.currency || currency)})` : 'Отмечено как оплачено');
      fetchData();
    } catch { toast.error('Ошибка'); }
    setPayingId(null);
  };

  const uploadReceipt = async (id, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.put(`/appointments/${id}/upload-receipt`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Чек загружен');
      fetchData();
    } catch { toast.error('Ошибка загрузки'); }
  };

  const toggleTax = async (id, done) => {
    try {
      await api.put(`/appointments/${id}/tax-receipt?done=${done}`, {});
      toast.success(done ? 'Чек пробит' : 'Отметка снята');
      fetchData();
    } catch { toast.error('Ошибка'); }
  };

  const addExpense = async () => {
    if (!expForm.amount || !expForm.date) { toast.error('Заполните сумму и дату'); return; }
    try {
      await api.post(`/expenses`, { ...expForm, amount: parseFloat(expForm.amount) });
      toast.success('Расход добавлен');
      setExpenseOpen(false);
      setExpForm({ category: 'supervision', amount: '', description: '', date: format(new Date(), 'yyyy-MM-dd') });
      fetchData();
    } catch { toast.error('Ошибка'); }
  };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Расход удалён');
      fetchData();
    } catch { toast.error('Ошибка'); }
  };

  const copyReminder = (appt) => {
    const text = `Добрый день, ${appt.patient_name}! Напоминаю об оплате за сеанс ${appt.date} (${appt.format_name}). Буду благодарна за перевод. Спасибо!`;
    navigator.clipboard.writeText(text);
    toast.success('Напоминание скопировано');
  };

  const incomeChange = summary && summary.prev_income > 0
    ? Math.round(((summary.income - summary.prev_income) / summary.prev_income) * 100) : null;
  const totalsByCurrency = summary?.totals_by_currency || [];
  const renderTotals = (field) => {
    const nonZero = totalsByCurrency.filter(item => Number(item[field] || 0) !== 0);
    const visible = nonZero.length > 0 ? nonZero : [{ currency, [field]: 0 }];
    return (
      <div className="space-y-0.5">
      {visible.map(item => (
        <div key={item.currency}>{formatMoney(item[field] || 0, item.currency)}</div>
      ))}
      </div>
    );
  };
  const renderNonZeroTotals = (field) => {
    const nonZero = totalsByCurrency.filter(item => Number(item[field] || 0) !== 0);
    return nonZero.length > 0
      ? nonZero.map(item => formatMoney(item[field], item.currency)).join(' · ')
      : formatMoney(0, currency);
  };

  return (
    <div data-testid="finance-page" className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
        <div className="max-w-full lg:max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 animate-fade-in">
            <h1 className="font-heading text-2xl lg:text-3xl text-stone-200">Финансы</h1>
            <div className="flex gap-1 bg-stone-800/50 rounded-lg p-1 border border-stone-800">
              {PERIODS.map(p => (
                <Button key={p.id} data-testid={`period-${p.id}`} variant={period === p.id ? 'default' : 'ghost'} size="sm"
                  onClick={() => setPeriod(p.id)}
                  className={period === p.id ? 'bg-amber-700 hover:bg-amber-600 text-white' : 'text-stone-500 hover:text-stone-200'}>
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          {loading ? <p className="text-stone-500 text-center py-12">Загрузка...</p> : summary && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 animate-fade-in delay-1">
                <SummaryCard label="Доход" value={renderTotals('income')} icon={TrendingUp} color="text-green-400" change={totalsByCurrency.length === 1 ? incomeChange : null} />
                <SummaryCard label="Расходы" value={renderTotals('expenses')} icon={TrendingDown} color="text-red-400" />
                <SummaryCard label="Чистая прибыль" value={renderTotals('profit')} icon={Wallet} color="text-amber-400" />
                <SummaryCard label={`Налог НПД (${summary.tax_rate}%)`} value={renderTotals('tax')} icon={Receipt} color="text-stone-400" />
              </div>

              {/* Awaiting Payment */}
              {(summary?.awaiting_payment || []).length > 0 && (
                <Card className="bg-amber-950/20 border-amber-900/40 mb-6 animate-fade-in delay-2">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle size={16} className="text-amber-500" />
                      <h3 className="text-amber-400 text-sm font-semibold">Ожидают оплаты — {renderNonZeroTotals('awaiting_total')}</h3>
                    </div>
                    <div className="space-y-3">
                      {(summary?.awaiting_payment || []).map(a => (
                        <div key={a.id} data-testid={`awaiting-${a.id}`} className="p-3 rounded-lg bg-stone-900/60 border border-stone-800/50 space-y-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-stone-200 text-sm font-medium">{a.patient_name}</p>
                              <p className="text-stone-500 text-xs mt-0.5">{a.date} в {a.time} — {a.format_name}</p>
                            </div>
                            <p className="text-amber-400 text-sm font-semibold whitespace-nowrap">
                              {formatMoney(a.amount || 0, a.currency || currency)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button data-testid={`pay-btn-${a.id}`} size="sm" onClick={() => markPaid(a.id)} disabled={payingId === a.id} className="bg-green-800 hover:bg-green-700 text-white h-8 text-xs">
                              <Check size={12} className="mr-1" /> Оплачено
                            </Button>
                            <label className="cursor-pointer">
                              <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => e.target.files?.[0] && uploadReceipt(a.id, e.target.files[0])} />
                              <span className="inline-flex items-center gap-1 px-2.5 h-8 rounded-md text-xs bg-stone-800 text-stone-300 hover:bg-stone-700 transition-colors">
                                <Upload size={12} /> Чек
                              </span>
                            </label>
                            <Button variant="ghost" size="sm" onClick={() => toggleTax(a.id, !a.tax_receipt_done)}
                              className={`h-8 px-2 gap-1 text-xs ${a.tax_receipt_done ? 'text-green-400' : 'text-stone-600 hover:text-amber-400'}`}
                              title={a.tax_receipt_done ? 'Чек в Мой Налог пробит' : 'Отметить чек в Мой Налог'}>
                              <FileCheck size={13} /> <span className="hidden sm:inline">{a.tax_receipt_done ? 'Пробит' : 'Мой Налог'}</span>
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => copyReminder(a)} className="text-stone-500 hover:text-amber-400 h-8 px-2 gap-1 text-xs" title="Скопировать напоминание">
                              <Copy size={13} /> <span className="hidden sm:inline">Напомнить</span>
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Charts Row */}
              {analytics && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 animate-fade-in delay-3">
                  {/* Monthly Income/Expenses */}
                  <Card className="bg-stone-900 border-stone-800">
                    <CardContent className="p-4">
                      <h3 className="text-stone-300 text-sm font-semibold mb-4">Доходы и расходы по месяцам</h3>
                      {(analytics.excluded_currencies || []).length > 0 && (
                        <p className="text-stone-600 text-xs mb-3">
                          Графики показаны в {analytics.currency || currency}; остальные валюты доступны в итогах выше.
                        </p>
                      )}
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={analytics.monthly}>
                          <XAxis dataKey="month" tick={{ fill: '#78716c', fontSize: 11 }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fill: '#78716c', fontSize: 11 }} axisLine={false} tickLine={false} width={50} />
                          <Tooltip contentStyle={{ background: '#1c1917', border: '1px solid #44403c', borderRadius: 8, color: '#d6d3d1' }} />
                          <Bar dataKey="income" fill="#059669" radius={[4, 4, 0, 0]} name="Доход" />
                          <Bar dataKey="expenses" fill="#dc2626" radius={[4, 4, 0, 0]} name="Расход" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Income by Type */}
                  <Card className="bg-stone-900 border-stone-800">
                    <CardContent className="p-4">
                      <h3 className="text-stone-300 text-sm font-semibold mb-4">Структура доходов</h3>
                      {(analytics?.income_by_type || []).length === 0 ? (
                        <p className="text-stone-600 text-center py-8 text-sm">Нет данных</p>
                      ) : (
                        <div className="flex items-center gap-4">
                          <ResponsiveContainer width="50%" height={180}>
                            <PieChart>
                              <Pie data={analytics?.income_by_type || []} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3}>
                                {(analytics?.income_by_type || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                              </Pie>
                              <Tooltip contentStyle={{ background: '#1c1917', border: '1px solid #44403c', borderRadius: 8, color: '#d6d3d1' }} formatter={(v) => fmtMoney(v)} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="flex-1 space-y-2">
                            {(analytics?.income_by_type || []).map((item, i) => (
                              <div key={item.name} className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                                <span className="text-stone-400 text-xs truncate">{item.name}</span>
                                <span className="text-stone-200 text-xs font-medium ml-auto">{fmtMoney(item.value)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Stats Row */}
              {analytics && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 animate-fade-in delay-4">
                  {/* Attendance */}
                  <Card className="bg-stone-900 border-stone-800">
                    <CardContent className="p-4">
                      <h3 className="text-stone-300 text-sm font-semibold mb-3">Доходимость</h3>
                      <div className="grid grid-cols-4 gap-3">
                        <StatBlock label="Всего" value={analytics.attendance.total} />
                        <StatBlock label="Состоялись" value={analytics.attendance.completed} color="text-green-400" />
                        <StatBlock label="Отменены" value={analytics.attendance.cancelled} color="text-red-400" />
                        <StatBlock label="Неявки" value={analytics.attendance.no_show} color="text-violet-400" />
                      </div>
                      <div className="mt-3 h-2 bg-stone-800 rounded-full overflow-hidden">
                        <div className="h-full bg-green-600 rounded-full" style={{ width: `${analytics.attendance.rate}%` }} />
                      </div>
                      <p className="text-stone-500 text-xs mt-1">{analytics.attendance.rate}% состоялись</p>
                    </CardContent>
                  </Card>

                  {/* LTV */}
                  <Card className="bg-stone-900 border-stone-800">
                    <CardContent className="p-4">
                      <h3 className="text-stone-300 text-sm font-semibold mb-3">Топ клиентов (LTV)</h3>
                      {(analytics?.ltv || []).length === 0 ? (
                        <p className="text-stone-600 text-center py-4 text-sm">Нет данных</p>
                      ) : (
                        <div className="space-y-2">
                          {(analytics?.ltv || []).map((c, i) => (
                            <div key={c.name} className="flex items-center gap-3">
                              <span className="text-stone-600 text-xs w-5 text-right">{i + 1}.</span>
                              <span className="text-stone-300 text-sm truncate flex-1">{c.name}</span>
                              <span className="text-amber-400 text-sm font-medium">{fmtMoney(c.value)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Expenses */}
              <Card className="bg-stone-900 border-stone-800 animate-fade-in delay-4">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-stone-300 text-sm font-semibold">Расходы</h3>
                    <Button data-testid="add-expense-btn" size="sm" onClick={() => setExpenseOpen(true)} className="bg-stone-800 hover:bg-stone-700 text-stone-300 h-8">
                      <Plus size={14} className="mr-1" /> Добавить
                    </Button>
                  </div>
                  {expenses.length === 0 ? (
                    <p className="text-stone-600 text-center py-6 text-sm">Нет расходов за период</p>
                  ) : (
                    <div className="space-y-2">
                      {expenses.map(e => (
                        <div key={e.id} data-testid={`expense-${e.id}`} className="flex items-center justify-between p-3 rounded-lg bg-stone-950/40 border border-stone-800/30">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-stone-800 text-stone-400 border-stone-700 text-[10px]">{catLabel(e.category)}</Badge>
                              <span className="text-red-400 text-sm font-medium">-{formatMoney(e.amount, e.currency || currency)}</span>
                            </div>
                            {e.description && <p className="text-stone-600 text-xs mt-1 truncate">{e.description}</p>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-stone-600 text-xs">{e.date}</span>
                            <Button variant="ghost" size="sm" onClick={() => deleteExpense(e.id)} className="text-stone-600 hover:text-red-400 h-7 w-7 p-0">
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Salary Split for Owner */}
              <SalarySplitSection currency={currency} />
            </>
          )}
        </div>
      </main>

      {/* Add Expense Dialog */}
      <Dialog open={expenseOpen} onOpenChange={setExpenseOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200">
          <DialogHeader><DialogTitle className="font-heading text-stone-200">Добавить расход</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-stone-400 text-sm font-medium mb-2 block">Категория</label>
              <Select value={expForm.category} onValueChange={v => setExpForm({ ...expForm, category: v })}>
                <SelectTrigger className="bg-stone-950/50 border-stone-800 text-stone-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-800">
                  {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-stone-400 text-sm font-medium mb-2 block">Сумма, {currencySymbol(currency)}</label>
              <Input data-testid="expense-amount" type="number" value={expForm.amount} onChange={e => setExpForm({ ...expForm, amount: e.target.value })} className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="5000" />
            </div>
            <div>
              <label className="text-stone-400 text-sm font-medium mb-2 block">Описание</label>
              <Input data-testid="expense-desc" value={expForm.description} onChange={e => setExpForm({ ...expForm, description: e.target.value })} className="bg-stone-950/50 border-stone-800 text-stone-200" placeholder="Необязательно" />
            </div>
            <div>
              <label className="text-stone-400 text-sm font-medium mb-2 block">Дата</label>
              <Input data-testid="expense-date" type="date" value={expForm.date} onChange={e => setExpForm({ ...expForm, date: e.target.value })} className="bg-stone-950/50 border-stone-800 text-stone-200" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setExpenseOpen(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">Отмена</Button>
            <Button data-testid="expense-save" onClick={addExpense} className="bg-amber-700 hover:bg-amber-600 text-white">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, change }) {
  return (
    <Card className="bg-stone-900 border-stone-800">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-stone-500 text-xs uppercase tracking-wider">{label}</span>
          <Icon size={16} className={color} />
        </div>
        <div className={`text-xl font-bold ${color}`}>{value}</div>
        {change !== null && change !== undefined && (
          <p className={`text-xs mt-1 ${change >= 0 ? 'text-green-500' : 'text-red-400'}`}>
            {change >= 0 ? '+' : ''}{change}% к прошл. периоду
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function StatBlock({ label, value, color = 'text-stone-200' }) {
  return (
    <div className="text-center">
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      <p className="text-stone-600 text-[10px] uppercase tracking-wider">{label}</p>
    </div>
  );
}


function SalarySplitSection({ currency }) {
  const [rules, setRules] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editDoc, setEditDoc] = useState('');
  const [editPct, setEditPct] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(({ data }) => {
      if (data.role === 'owner' && data.active_features?.includes('salary_split')) {
        setIsOwner(true);
        Promise.all([
          api.get('/salary/rules'),
          api.get('/team/doctors'),
        ]).then(([rRes, dRes]) => {
          setRules(Array.isArray(rRes.data) ? rRes.data : []);
          setDoctors(Array.isArray(dRes.data) ? dRes.data : []);
        }).catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const saveRule = async () => {
    if (!editDoc || !editPct) return;
    setSaving(true);
    try {
      const existing = rules.find(r => r.doctor_id === editDoc);
      if (existing) {
        await api.put(`/salary/rules/${existing.id}`, { doctor_share: parseFloat(editPct) });
        setRules(prev => prev.map(r => r.doctor_id === editDoc ? { ...r, doctor_share: parseFloat(editPct) } : r));
      } else {
        const { data } = await api.post('/salary/rules', { doctor_id: editDoc, doctor_share: parseFloat(editPct) });
        setRules(prev => [...prev, data]);
      }
      setEditDoc(''); setEditPct('');
      toast.success('Ставка сохранена');
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    setSaving(false);
  };

  const deleteRule = async (ruleId) => {
    try {
      await api.delete(`/salary/rules/${ruleId}`);
      setRules(prev => prev.filter(r => r.id !== ruleId));
      toast.success('Удалено');
    } catch { toast.error('Ошибка'); }
  };

  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const renderReportTotals = (field, color) => {
    const totals = (reportData?.totals_by_currency || []).filter(item => Number(item[field] || 0) !== 0);
    const visible = totals.length > 0 ? totals : [{ currency: reportData?.currency || currency, [field]: 0 }];
    return (
      <div className={`${color} text-lg font-bold`}>
        {visible.map(item => <div key={item.currency}>{formatMoney(item[field], item.currency)}</div>)}
      </div>
    );
  };

  const generateReport = async () => {
    setReportLoading(true);
    const now = new Date();
    const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const to = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${lastDay}`;
    try {
      const { data } = await api.get(`/salary/report?date_from=${from}&date_to=${to}`);
      setReportData(data);
    } catch { toast.error('Ошибка формирования отчёта'); }
    setReportLoading(false);
  };

  if (!isOwner || loading) return null;

  return (
    <>
    <Card className="bg-stone-900 border-stone-800 mt-6">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="font-heading text-stone-200 text-lg">Ставки специалистов (KPI)</CardTitle>
            <p className="text-stone-500 text-xs mt-1">Укажите % от дохода, который остаётся специалисту</p>
          </div>
          <Button size="sm" onClick={generateReport} disabled={reportLoading}
            className="bg-teal-700 hover:bg-teal-600 text-white">
            {reportLoading ? 'Загрузка...' : 'Отчёт по зарплатам'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {rules.length > 0 && (
          <div className="space-y-2">
            {rules.map(r => {
              const doc = doctors.find(d => d.id === r.doctor_id);
              const share = r.doctor_share ?? r.percentage ?? 0;
              return (
                <div key={r.id} className="flex items-center justify-between p-3 rounded-lg bg-stone-950/30 border border-stone-800/50">
                  <div>
                    <p className="text-stone-200 text-sm font-medium">{doc?.name || r.doctor_id}</p>
                    <p className="text-stone-500 text-xs">{share}% специалисту / {100 - share}% организации</p>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 text-xs"
                    onClick={() => deleteRule(r.id)}>Удалить</Button>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="text-stone-500 text-xs mb-1 block">Специалист</label>
            <select value={editDoc} onChange={e => setEditDoc(e.target.value)}
              className="w-full rounded-md bg-stone-950/50 border border-stone-800 text-stone-200 p-2 text-sm">
              <option value="">Выберите...</option>
              {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="w-24">
            <label className="text-stone-500 text-xs mb-1 block">% специалисту</label>
            <Input type="number" min="0" max="100" value={editPct} onChange={e => setEditPct(e.target.value)}
              className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm" placeholder="70" />
          </div>
          <Button size="sm" onClick={saveRule} disabled={saving || !editDoc || !editPct}
            className="bg-amber-700 hover:bg-amber-600 text-white mb-0.5">
            {saving ? '...' : 'Сохранить'}
          </Button>
        </div>
      </CardContent>
    </Card>

    {/* Salary Report */}
    {reportData && (
      <Card className="bg-stone-900 border-teal-900/30 mt-4">
        <CardHeader>
          <CardTitle className="font-heading text-stone-200 text-lg flex items-center gap-2">
            Отчёт по зарплатам
            <Badge className="bg-teal-900/30 text-teal-400 border-teal-800/40 text-[10px]">
              {reportData.period?.from?.split('-').reverse().join('.')} — {reportData.period?.to?.split('-').reverse().join('.')}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-stone-950/30 border border-stone-800/50 text-center">
              <p className="text-stone-500 text-[10px] uppercase">Общий доход</p>
              {renderReportTotals('total_income', 'text-stone-200')}
            </div>
            <div className="p-3 rounded-lg bg-stone-950/30 border border-stone-800/50 text-center">
              <p className="text-stone-500 text-[10px] uppercase">К выплате врачам</p>
              {renderReportTotals('total_to_doctors', 'text-amber-400')}
            </div>
            <div className="p-3 rounded-lg bg-stone-950/30 border border-stone-800/50 text-center">
              <p className="text-stone-500 text-[10px] uppercase">Остаётся клинике</p>
              {renderReportTotals('total_to_clinic', 'text-teal-400')}
            </div>
          </div>

          {/* By doctor */}
          {(reportData.by_doctor || []).length > 0 ? (
            <div className="space-y-2">
              {reportData.by_doctor.map(d => (
                <div key={`${d.doctor_id}-${d.currency || currency}`} className="p-4 rounded-lg bg-stone-950/30 border border-stone-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-stone-200 text-sm font-medium">{d.doctor_name}</p>
                      <p className="text-stone-600 text-xs">Ставка: {d.doctor_share}%</p>
                    </div>
                    <div className="text-right">
                      <p className="text-amber-400 text-lg font-bold">{formatMoney(d.doctor_amount, d.currency || currency)}</p>
                      <p className="text-stone-500 text-[10px]">к выплате</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-stone-500">
                    <span>{d.appointments} приёмов</span>
                    <span>на {formatMoney(d.total, d.currency || currency)}</span>
                    <span>клинике: {formatMoney(d.clinic_amount, d.currency || currency)}</span>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-2 h-1.5 rounded-full bg-stone-800 overflow-hidden">
                    <div className="h-full rounded-full bg-amber-600" style={{ width: `${d.doctor_share}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-stone-600 text-sm text-center py-4">Нет оплаченных/завершённых приёмов за этот период</p>
          )}
        </CardContent>
      </Card>
    )}
    </>
  );
}
